import {
    Button,
    Checkbox,
    CheckboxOnChangeData,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogOpenChangeData,
    DialogOpenChangeEvent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    tokens,
    Tooltip,
} from '@fluentui/react-components';
import { DialogOpenChangeEventHandler } from '@fluentui/react-dialog';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { getComponent, NumberFormatOptions } from '@syncfusion/ej2-base';
import {
    Aggregate,
    AggregateColumnDirective,
    AggregateColumnsDirective,
    AggregateDirective,
    AggregatesDirective,
    CheckBoxFilterBase,
    ColumnDirective,
    ColumnsDirective,
    ContextMenu,
    Edit,
    EditSettingsModel,
    Filter,
    FilterSettingsModel,
    GridComponent,
    IEditCell,
    Inject,
    Page,
    Sort,
} from '@syncfusion/ej2-react-grids';
import { NumericTextBox, NumericTextBoxComponent, NumericTextBoxModel } from '@syncfusion/ej2-react-inputs';
import React, { ChangeEvent, FC, KeyboardEventHandler, MutableRefObject, RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GridStateContextProvider } from '../../contexts/gridStateContext';
import { useEnterAmountsData } from '../../hooks/useEnterAmountsData';
import { useEnterAmountsDialogStyles } from '../../hooks/useEnterAmountsDialogStyles';
import { IEnterAmountsGridRowData, useEnterAmountsGridComponentProps } from '../../hooks/useEnterAmountsGridComponentProps';
import { useFunctionalAccess } from '../../hooks/useFunctionalAccess';

import { useQueryClient } from '@tanstack/react-query';
import { getGetJournalEntriesQueryKey } from '../../api/amount/amount';
import { useIsFeatureEnabled } from '../../api/features/features';
import { getGetAmountsEntriesQueryKey } from '../../api/report/report';
import { AllowNull } from '../../data-types/AllowUndefinedAndNull';
import {
    IEnterAmountsDialogFilterChangeTrackingInformation,
    IEnterAmountsDialogInitialLoadTrackingInformation,
    IEnterAmountsDialogSaveAmountsTrackingInformation,
    usePerformanceMetricsForEnterAmountsDialog,
} from '../../hooks/usePerformanceMetricsForEnterAmountsDialog';
import { usePreventNavigation } from '../../hooks/usePreventNavigation';
import {
    ExtractionDetailsAccountSummary,
    ExtractionDetailsAdjustmentInfo,
    IAccountAmountRow,
    IReportColumnContext,
    IWhitePaperReportChanges,
    ProblemDetails,
    SaveAmountsRequest,
    SaveAmountsResponse,
} from '../../model';
import { ConfirmationDialogType, EnterAmountsModalActionActionType } from '../../reducers/enterAmountsDialogReducer';
import { ENTRY_POINT_DIALOG_CLOSE } from '../../telemetry/entryPoints';
import { ACTION_INVOKED_EVENT, ENTER_AMOUNTS_DIALOG_CLOSED } from '../../telemetry/events';
import { useEventTracker } from '../../telemetry/hooks/useEventTracker';
import { addEnterAmountsUpdatedAccountsToTrackingData, addReportCellToChangeTrackingData } from '../../utils/ChangeTrackingDataUtils';
import {
    getSyncfusionFocusEventHandlerHack,
    getSyncfusionPopupMenuDismissHack,
    ISyncfusionGridPopupMenuDismissHack,
} from '../../utils/DialogUtilsV9';
import { deFormatNumber } from '../../utils/NumberUtils';
import { ErrorType } from '../../utils/UseCustomInstance';
import LoadingSpinner from '../common/LoadingSpinner';
import ConfirmationDialogV9 from '../dialog/ConfirmationDialogV9';
import { JournalEntryGrid } from '../sidebar/JournalEntryGrid';
import { accountAmountRowPropertyNames } from './AccountAmountRowPropertyNames';
import { AdjustmentCombobox } from './AdjustmentCombobox';
import { CalendarPeriodCombobox } from './CalendarPeriodCombobox';
import { CaseCombobox } from './CaseCombobox';
import { EntityCombobox } from './EntityCombobox';
import { JurisdictionCombobox } from './JurisdictionCombobox';

export interface IEnterAmountsDialogProps {
    adjustmentCode: string;
    case: string;
    jurisdiction: string;
    entity: string;
    period: string;
    location: string;
    drillDownRowNumber: number;
    definingRowNumber?: number;
    columnId: number;
    changeTrackingData: IWhitePaperReportChanges;
    taxReturnKey: number;
    rowDescription: string;
    onClose?: (amountsChangedOnServer: boolean, changeTrackingData: IWhitePaperReportChanges) => void;
    buttonTrigger?: HTMLElement;
    isInputRow?: boolean;
}

export function getUpdatedAccountAmountRow(
    originalRow: IAccountAmountRow,
    batchChanges: { changedRecords: IAccountAmountRow[] }
): IAccountAmountRow | undefined {
    const updatedRow: IAccountAmountRow | undefined = batchChanges.changedRecords.find(
        (changedRecord) => changedRecord.accountCode === originalRow.accountCode
    );
    return updatedRow;
}
export const enterAmountsModalId: string = 'enterAmountsDialog';
const trackingDataNotAvailableValue: string = 'N/A';
const ColumnDividerLine: React.FC = () => {
    return (
        <svg xmlns='http://www.w3.org/2000/svg' width='7' height='412' viewBox='0 0 7 412' fill='none'>
            <path
                d='M1.00002 -2.18556e-07L1.00001 186.736C1.00001 187.058 1.07786 187.375 1.22692 187.661L5.54771 195.941C5.8331 196.488 5.85015 197.136 5.59393 197.697L1.1807 207.362C1.06163 207.623 1.00001 207.906 1.00001 208.193L1 412'
                stroke={tokens.colorNeutralStroke1}
            />
        </svg>
    );
};
const EnterAmountsDialog: FC<IEnterAmountsDialogProps> = (props: IEnterAmountsDialogProps) => {
    const queryClient = useQueryClient();
    const {
        getEnterAmountsDialogFilterChangeTrackingInformation,
        getEnterAmountsDialogInitialLoadTrackingInformation,
        getEnterAmountsDialogSaveAmountsTrackingInformation,
        trackEnterAmountsDialogFilterChangeBegin,
        trackEnterAmountsDialogFilterChangeEnd,
        trackEnterAmountsDialogInitialLoadBegin,
        trackEnterAmountsDialogInitialLoadEnd,
        trackEnterAmountsDialogSaveAmountsBegin,
        trackEnterAmountsDialogSaveAmountsEnd,
    } = usePerformanceMetricsForEnterAmountsDialog();
    const { t } = useTranslation();
    const { trackEvent } = useEventTracker();
    const gridRef: RefObject<GridComponent> = useRef<GridComponent>(null);
    const gridFilterMenuRef: MutableRefObject<AllowNull<CheckBoxFilterBase>> = useRef<CheckBoxFilterBase>(null);
    const {
        accountAmountRows,
        cases,
        currentlyDisplayedCase,
        datasetCase,
        entities,
        currentlyDisplayedEntity,
        datasetEntity,
        jurisdictions,
        currentlyDisplayedJurisdiction,
        datasetJurisdiction,
        years,
        currentlyDisplayedYear,
        datasetYear,
        adjustments,
        currentlyDisplayedAdjustment,
        datasetAdjustment,
        saveAmounts,
        updateDialogState,
        isLoading,
        saveButtonIsDisabled,
        applyButtonIsDisabled,
        changeTrackingData,
        onFilterApply,
        dispatchFiltersUIState,
        onFilterReset,
        amountsOnly,
        combineDebitCreditColumns,
        showAllAccounts,
        loadStateChangeEvent,
        journalEntryGridProps,
        journalEntryGridRef,
        journalEntryGridPopupMenuDismissHack,
        showConfirmationDialog,
        confirmationDialogType,
        isFilterReset,
        isDirty,
        refetchAmounts,
        onDiscardConfirmation,
        isFilteredEnterAmountsEnabled,
        pendingJournalEntryArgs,
    } = useEnterAmountsData(props);

    // Block navigation when there are unsaved changes (shared blocker from NavigationBlockerProvider).
    const { blocker, setShouldBlockNavigation } = usePreventNavigation();
    const closeEnterAmountsDialog = useCallback(
        (amountsChangedOnServer: boolean, updatedChangeTrackingData: IWhitePaperReportChanges) => {
            trackEvent(ACTION_INVOKED_EVENT, { action_name: ENTER_AMOUNTS_DIALOG_CLOSED, entry_point: ENTRY_POINT_DIALOG_CLOSE });
            props.onClose?.(amountsChangedOnServer, updatedChangeTrackingData);
        },
        [props.onClose, trackEvent]
    );
    // Show confirmation dialog when navigation is blocked
    useEffect(() => {
        if (blocker?.state === 'blocked' && !showConfirmationDialog && !isLoading) {
            updateDialogState({
                type: EnterAmountsModalActionActionType.ConfirmationDialogOpened,
                dialogType: ConfirmationDialogType.UnsavedChanges,
            });
        }
    }, [blocker?.state, showConfirmationDialog, updateDialogState, isLoading]);

    if (isLoading && loadStateChangeEvent) {
        if (loadStateChangeEvent === 'Initialize') {
            const createInitialLoadTrackingInformation: () => IEnterAmountsDialogInitialLoadTrackingInformation = () => {
                return {
                    adjustment: datasetAdjustment?.adjustmentCode! ?? trackingDataNotAvailableValue,
                    case: datasetCase?.code! ?? trackingDataNotAvailableValue,
                    columnId: props.columnId.toString(),
                    entity: datasetEntity?.code! ?? trackingDataNotAvailableValue,
                    jurisdiction: datasetJurisdiction?.code! ?? trackingDataNotAvailableValue,
                    numberOfAccounts: 0,
                    reportId: props.taxReturnKey.toString(),
                    rowNumber: props.definingRowNumber?.toString() ?? props.drillDownRowNumber.toString(),
                    success: false,
                    year: datasetYear?.periodYear?.toString() ?? trackingDataNotAvailableValue,
                };
            };

            trackEnterAmountsDialogInitialLoadBegin(createInitialLoadTrackingInformation);
        } else if (loadStateChangeEvent === 'FilterChange') {
            const createFilterChangeTrackingInformation: () => IEnterAmountsDialogFilterChangeTrackingInformation = () => {
                return {
                    adjustment: datasetAdjustment?.adjustmentCode! ?? trackingDataNotAvailableValue,
                    case: datasetCase?.code! ?? trackingDataNotAvailableValue,
                    columnId: props.columnId.toString(),
                    entity: datasetEntity?.code! ?? trackingDataNotAvailableValue,
                    jurisdiction: datasetJurisdiction?.code! ?? trackingDataNotAvailableValue,
                    numberOfAccounts: 0,
                    reportId: props.taxReturnKey.toString(),
                    rowNumber: props.definingRowNumber?.toString() ?? props.drillDownRowNumber.toString(),
                    success: false,
                    year: datasetYear?.periodYear?.toString() ?? trackingDataNotAvailableValue,
                };
            };

            trackEnterAmountsDialogFilterChangeBegin(createFilterChangeTrackingInformation);
        } else if (loadStateChangeEvent === 'SaveAmounts') {
            const createSaveAmountsTrackingInformation: () => IEnterAmountsDialogSaveAmountsTrackingInformation = () => {
                return {
                    columnId: props.columnId.toString(),
                    numberOfAccountsSaved: 0,
                    numberOfAmountsSaved: 0,
                    reportId: props.taxReturnKey.toString(),
                    rowNumber: props.definingRowNumber?.toString() ?? props.drillDownRowNumber.toString(),
                    success: false,
                };
            };

            trackEnterAmountsDialogSaveAmountsBegin(createSaveAmountsTrackingInformation);
        }
    }
    const { data: isJournalEntryEnabled } = useIsFeatureEnabled({ featureFlagName: 'JournalEntryEnabled' });
    const { functionalAccess } = useFunctionalAccess();
    const {
        onCellSave,
        onGridCreated,
        onCellEdit,
        onRowDataBound,
        onGridDataBound,
        onKeyPressed,
        contextMenuItems,
        handleContextMenuItemClick,
        handleContextMenuOpen,
    } = useEnterAmountsGridComponentProps(
        gridRef,
        updateDialogState,
        saveButtonIsDisabled,
        journalEntryGridRef,
        journalEntryGridPopupMenuDismissHack,
        functionalAccess!,
        isJournalEntryEnabled ?? false,
        isDirty,
        setShouldBlockNavigation
    );
    const {
        modalStyles,
        dialogContentStyles,
        gridComponentCSSClass,
        totalRowStyle,
        spinnerContainerClassName,
        dialogBodyStyles,
        headingStyles,
        subHeadingStyles,
        checkboxGroupStyles,
        enterAmountsDialogRootStyles,
        filterButtonWrapper,
        filterWrapper,
        gridWrapper,
        filterColumnStyles,
    } = useEnterAmountsDialogStyles();
    const closeLabel = useMemo(() => t('close'), [t]);
    const cancelLabel = useMemo(() => t('cancel'), [t]);
    const confirmLabel = useMemo(() => t('save'), [t]);
    const enterAmountsHeaderText = useMemo(() => t('enterAmountsHeader'), [t]);
    const journalEntryHeaderText = useMemo(() => t('journalEntry'), [t]);
    const backLabel: string = useMemo(() => t('back'), [t]);

    const primaryHandler = useCallback(
        (_event: React.MouseEvent<HTMLElement>, closeAfterSave: boolean = false) => {
            function retrieveNormalizedRowValue(row: IAccountAmountRow): string {
                let amount: string = '0';

                if (row.accountBalanceType === 'Debit') {
                    if (row.debitTotal) {
                        amount = row.debitTotal.toString();
                    } else if (row.creditTotal) {
                        amount = (row.creditTotal * -1).toString();
                    }
                } else if (row.accountBalanceType === 'Credit') {
                    if (row.creditTotal) {
                        amount = row.creditTotal.toString();
                    } else if (row.debitTotal) {
                        amount = (row.debitTotal * -1).toString();
                    }
                } else {
                    throw new Error(`Invalid account balance type: ${row.accountBalanceType}`);
                }

                return amount;
            }

            function convertIAccountAmountRowToExtractionDetailsAccountSummary(row: IAccountAmountRow): ExtractionDetailsAccountSummary {
                const amount: string = retrieveNormalizedRowValue(row);

                return {
                    accountNumber: row.accountCode,
                    adjustmentInfo: [
                        {
                            adjustmentCode: datasetAdjustment?.adjustmentCode,
                            case: datasetCase?.code,
                            corporation: datasetEntity?.code,
                            jurisdiction: datasetJurisdiction?.code,
                            location: datasetJurisdiction?.code,
                            reportAmount: amount,
                            year: datasetYear?.periodYear?.toString(),
                        },
                    ],
                    reportAmount: amount,
                };
            }

            function createSaveAmountsRequestIfRowsChanged() {
                const updatedRows: IAccountAmountRow[] = findUpdatedRows();

                if (updatedRows.length === 0) {
                    // Nothing to save.
                    return null;
                }

                const accountSummaryUpdates = updatedRows.map(convertIAccountAmountRowToExtractionDetailsAccountSummary);
                let updatedChangeTrackingData: IWhitePaperReportChanges | undefined;

                if (props.definingRowNumber) {
                    const currentContext: IReportColumnContext = {
                        case: datasetCase!.code!,
                        entity: datasetEntity!.code!,
                        jurisdiction: datasetJurisdiction!.code!,
                        year: datasetYear?.periodYear!,
                    };

                    updatedChangeTrackingData = addEnterAmountsUpdatedAccountsToTrackingData(
                        props.drillDownRowNumber,
                        props.columnId,
                        props.definingRowNumber,
                        currentContext,
                        datasetAdjustment?.adjustmentCode || '',
                        updatedRows.map((row) => row.accountCode!),
                        changeTrackingData
                    );
                } else {
                    updatedChangeTrackingData = addReportCellToChangeTrackingData(
                        props.drillDownRowNumber,
                        props.columnId,
                        changeTrackingData
                    );
                }

                const saveAmountsRequest: SaveAmountsRequest = {
                    accountSummaryUpdates: accountSummaryUpdates,
                    changesTrackingData: updatedChangeTrackingData,
                    taxReturnKey: props.taxReturnKey,
                };

                return saveAmountsRequest;
            }
            function findUpdatedRows(): IAccountAmountRow[] {
                const rowElements: IAccountAmountRow[] | undefined = accountAmountRows;
                const updatedRowData: IAccountAmountRow[] = [];
                if (rowElements) {
                    for (const rowElement of rowElements as IEnterAmountsGridRowData[]) {
                        if (rowElement?.isModified) {
                            rowElement.isModified = false;
                            updatedRowData.push(rowElement);
                        }
                    }
                }
                return updatedRowData;
            }
            const saveAmountsRequest: SaveAmountsRequest | null = createSaveAmountsRequestIfRowsChanged();

            if (saveAmountsRequest) {
                saveAmounts(
                    {
                        data: saveAmountsRequest,
                    },
                    {
                        onSettled: (
                            _data: SaveAmountsResponse | undefined,
                            error: ErrorType<ProblemDetails> | null,
                            variables: { data: SaveAmountsRequest },
                            _context: unknown | undefined
                        ) => {
                            if (!error) {
                                const numberOfAccountsSaved: number = variables.data.accountSummaryUpdates!.length;
                                const numberOfAmountsSaved: number = variables.data
                                    .accountSummaryUpdates!.filter((x: ExtractionDetailsAccountSummary) => x.adjustmentInfo)
                                    .flatMap((x: ExtractionDetailsAccountSummary) => x.adjustmentInfo!)
                                    .flatMap((x: ExtractionDetailsAdjustmentInfo) => x.entries).length;
                                const saveAmountsTrackingInformation: AllowNull<IEnterAmountsDialogSaveAmountsTrackingInformation> =
                                    getEnterAmountsDialogSaveAmountsTrackingInformation()!;

                                if (saveAmountsTrackingInformation) {
                                    saveAmountsTrackingInformation.numberOfAccountsSaved = numberOfAccountsSaved;
                                    saveAmountsTrackingInformation.numberOfAmountsSaved = numberOfAmountsSaved;
                                }
                            }
                        },
                        onSuccess: (
                            _data: SaveAmountsResponse | undefined,
                            variables: { data: SaveAmountsRequest },
                            _context: unknown | undefined
                        ) => {
                            queryClient.invalidateQueries({
                                queryKey: getGetAmountsEntriesQueryKey({
                                    CaseCode: datasetCase?.code ?? props.case,
                                    EntityCode: datasetEntity?.code ?? props.entity,
                                    Period: datasetYear?.periodYear?.toString() ?? props.period,
                                    AdjustmentCode: datasetAdjustment?.adjustmentCode ?? props.adjustmentCode,
                                    Jurisdiction: datasetJurisdiction?.code ?? props.jurisdiction,
                                }),
                            });
                            queryClient.invalidateQueries({ queryKey: getGetJournalEntriesQueryKey() });
                            if (closeAfterSave) {
                                variables.data.changesTrackingData && closeEnterAmountsDialog(true, variables.data.changesTrackingData);
                                if (blocker?.state === 'blocked') {
                                    blocker.proceed?.();
                                }
                                setShouldBlockNavigation(false);
                            } else {
                                updateDialogState({
                                    type: EnterAmountsModalActionActionType.AmountsSaved,
                                    changeTrackingData: variables.data.changesTrackingData!,
                                });
                                refetchAmounts();
                                if (blocker?.state === 'blocked') {
                                    blocker.reset?.();
                                }
                                setShouldBlockNavigation(false);
                            }
                        },
                    }
                );
            }
        },
        [
            updateDialogState,
            gridRef,
            gridRef.current,
            saveAmounts,
            datasetAdjustment,
            datasetCase,
            datasetEntity,
            datasetJurisdiction,
            datasetYear,
            changeTrackingData,
            props,
            accountAmountRows,
            blocker,
            setShouldBlockNavigation,
        ]
    );

    const editSettings: EditSettingsModel = {
        allowEditing: true,
        allowAdding: false,
        allowDeleting: false,
        showConfirmDialog: false,
        mode: 'Batch',
    };

    const numericEditParams: IEditCell = {
        params: {
            format: 'N',
            decimals: 0,
            showSpinButton: false,
            htmlAttributes: { maxlength: '15' },
            pasteHandler(e: ClipboardEvent) {
                // _this.setElementValue doesnt work when object is declared as NumericTextBox because functions are marked as private.
                //This code was given to us by Syncfusion in order to intercept paste values and change them before completing the paste event.
                const textBoxComponent = getComponent<NumericTextBoxComponent>(e.target! as HTMLElement, NumericTextBox) as any;
                const defaultValue = '';
                if (!textBoxComponent.enabled || textBoxComponent.readonly) {
                    return;
                }
                setTimeout(function () {
                    const number = deFormatNumber(textBoxComponent.element.value).trim();
                    textBoxComponent.setElementValue(number);
                    if (!textBoxComponent.numericRegex().test(textBoxComponent.element.value)) {
                        textBoxComponent.setElementValue(defaultValue);
                    }
                });
            },
            // this overrides the default keydown handler for the NumericTextBox, which increments and decrements the value by 1 on arrow up/down respectively
            // instead, we want to persist the value currently in the cell, stop editing, and navigate to the next cell
            keyDownHandler(e: KeyboardEvent) {
                if (e.code === 'ArrowDown' || e.code === 'ArrowUp') {
                    // all of this code comes directly from the Syncfusion NumericTextBox source code, which
                    // is why it is using deprecated function calls
                    const evt = document.createEvent('HTMLEvents');
                    evt.initEvent('change', false, true);
                    const textBoxComponent = getComponent<NumericTextBoxComponent>(e.target! as HTMLElement, NumericTextBox) as any;
                    textBoxComponent.element.dispatchEvent(evt);
                }
            },
            // this is the only way we are able to intercept the focusOut event on the NumericTextBox when a user hits the tab key
            // without this, the textbox will not go away, and keyboard focus will move outside of the grid
            focusOutHandler(_e: FocusEvent) {
                gridRef?.current?.editModule?.saveCell();
            },
        } as NumericTextBoxModel,
    };

    const numberFormatOptions = {
        format: 'N',
        useGrouping: true,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    } as NumberFormatOptions;

    const totalRowTemplate = useCallback(
        (_props: any) => {
            return <span style={totalRowStyle}>{t('total')}</span>;
        },
        [t, totalRowStyle]
    );

    const onDialogOpenChange: DialogOpenChangeEventHandler = (event: DialogOpenChangeEvent, data: DialogOpenChangeData) => {
        if (!data.open) {
            if (isDirty) {
                event.preventDefault();
                updateDialogState({
                    type: EnterAmountsModalActionActionType.ConfirmationDialogOpened,
                    dialogType: ConfirmationDialogType.UnsavedChanges,
                });
                return;
            }

            if (data.type === 'escapeKeyDown') {
                event.preventDefault();
                event.stopPropagation();
            }
            if (props.buttonTrigger) {
                props.buttonTrigger.focus();
            }
            closeEnterAmountsDialog(changeTrackingData !== props.changeTrackingData, changeTrackingData);
        }
    };

    const cancelButtonRef: RefObject<HTMLButtonElement> = useRef(null);

    const syncfusionFocusHack: KeyboardEventHandler<HTMLDivElement> = getSyncfusionFocusEventHandlerHack(cancelButtonRef);
    const syncfusionGridFilterDismissHack: ISyncfusionGridPopupMenuDismissHack = getSyncfusionPopupMenuDismissHack(
        gridRef,
        gridFilterMenuRef
    );

    const adjustedAmountFormat = '#,###;(#,###);0';
    const filterOptions: FilterSettingsModel = useMemo(
        () => ({
            type: 'Excel',
            enableInfiniteScrolling: true,
        }),
        []
    );
    const stringColumnFilterParams = useMemo(() => ({ params: { autofill: false } }), []);

    useEffect(() => {
        const enterAmountsGrid: GridComponent | null = gridRef.current;

        if (enterAmountsGrid) {
            const amountFilterApplied: boolean = !!((enterAmountsGrid.filterModule as any)?.actualPredicate as any)[
                accountAmountRowPropertyNames.adjustedTotal
            ];

            if (amountsOnly && !amountFilterApplied) {
                enterAmountsGrid.filterByColumn(accountAmountRowPropertyNames.adjustedTotal, 'notequal', null);
            } else if (!amountsOnly && amountFilterApplied) {
                enterAmountsGrid.clearFiltering([accountAmountRowPropertyNames.adjustedTotal]);
            }
        }
    }, [amountsOnly]);

    useEffect(() => {
        const enterAmountsGrid: GridComponent | null = gridRef.current;
        if (enterAmountsGrid) {
            const creditDebitColumnsAreCombined: boolean = combineDebitCreditColumns;
            if (creditDebitColumnsAreCombined) {
                enterAmountsGrid.showColumns(accountAmountRowPropertyNames.combinedTotal, 'field');
                enterAmountsGrid.hideColumns(
                    [accountAmountRowPropertyNames.debitTotal, accountAmountRowPropertyNames.creditTotal],
                    'field'
                );
            } else {
                enterAmountsGrid.hideColumns(accountAmountRowPropertyNames.combinedTotal, 'field');
                enterAmountsGrid.showColumns(
                    [accountAmountRowPropertyNames.debitTotal, accountAmountRowPropertyNames.creditTotal],
                    'field'
                );
            }
        }
    }, [gridRef, gridRef?.current, combineDebitCreditColumns]);

    const onDialogSurfaceClick = (event: React.MouseEvent<HTMLDivElement>) => {
        syncfusionGridFilterDismissHack.dialogSurfaceClick(event);
        journalEntryGridPopupMenuDismissHack.dialogSurfaceClick(event);
    };

    const clearEnterAmountsGridFilters = useCallback(() => {
        gridRef.current?.clearFiltering();
    }, []);

    const handleFilterReset = useCallback(() => {
        clearEnterAmountsGridFilters();
        onFilterReset();
    }, [clearEnterAmountsGridFilters, onFilterReset]);

    const handleDiscardConfirmation = useCallback(() => {
        if (isFilterReset) {
            clearEnterAmountsGridFilters();
        }
        onDiscardConfirmation();
    }, [isFilterReset, clearEnterAmountsGridFilters, onDiscardConfirmation]);

    const onAmountsOnlyChange = (_event: ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) => {
        const newFilterValue: boolean = data.checked ? true : false;
        updateDialogState({
            type: EnterAmountsModalActionActionType.FiltersUpdated,
            filterSelections: {
                amountsOnly: newFilterValue,
            },
        });
    };

    const onCombineDebitCreditColumnsChange = (_event: ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) => {
        const newFilterValue: boolean = data.checked ? true : false;
        updateDialogState({
            type: EnterAmountsModalActionActionType.FiltersUpdated,
            filterSelections: {
                combineDebitCreditColumns: newFilterValue,
            },
        });
    };

    const onShowAllAccountsChange = (_event: ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) => {
        const newFilterValue: boolean = data.checked ? true : false;
        updateDialogState({
            type: EnterAmountsModalActionActionType.FiltersUpdated,
            filterSelections: {
                showAllAccounts: newFilterValue,
            },
        });
    };

    const onConfirmationCancel = (_event: React.MouseEvent<HTMLElement>) => {
        updateDialogState({ type: EnterAmountsModalActionActionType.ConfirmationDialogClosed });
        blocker.reset?.();
    };
    const onConfirmationSave = (event: React.MouseEvent<HTMLElement>) => {
        if (confirmationDialogType === ConfirmationDialogType.UnsavedChanges) {
            // Save and close the modal after save completes
            updateDialogState({ type: EnterAmountsModalActionActionType.ConfirmationDialogClosed });
            primaryHandler(event, !pendingJournalEntryArgs);
        } else {
            // Filter reset scenario - save and apply filters
            primaryHandler(event);
            if (!isFilterReset) {
                updateDialogState({ type: EnterAmountsModalActionActionType.FiltersApplied });
            } else {
                handleFilterReset();
            }
            if (blocker?.state === 'blocked') {
                blocker.reset?.();
            }
        }

        if (pendingJournalEntryArgs) {
            updateDialogState({
                type: EnterAmountsModalActionActionType.JournalEntryOpened,
                accountCode: pendingJournalEntryArgs.accountCode,
                gridActionComplete: pendingJournalEntryArgs.gridActionComplete,
                gridRef: pendingJournalEntryArgs.gridRef,
            });
            if (blocker?.state === 'blocked') {
                blocker.reset?.();
            }
        }
    };
    const onConfirmationDismiss = (_event: React.MouseEvent<HTMLElement>) => {
        if (confirmationDialogType === ConfirmationDialogType.UnsavedChanges) {
            // Leave without saving - discard changes and close
            const queryKey = getGetAmountsEntriesQueryKey({
                CaseCode: datasetCase?.code ?? props.case,
                EntityCode: datasetEntity?.code ?? props.entity,
                Period: datasetYear?.periodYear?.toString() ?? props.period,
                AdjustmentCode: datasetAdjustment?.adjustmentCode ?? props.adjustmentCode,
                Jurisdiction: datasetJurisdiction?.code ?? props.jurisdiction,
            });
            queryClient.invalidateQueries({ queryKey });
            updateDialogState({ type: EnterAmountsModalActionActionType.ConfirmationDialogClosed });

            // If navigation was blocked, proceed with navigation
            if (blocker?.state === 'blocked') {
                blocker.proceed?.();
            }
            setShouldBlockNavigation(false);

            if (pendingJournalEntryArgs) {
                updateDialogState({
                    type: EnterAmountsModalActionActionType.JournalEntryOpened,
                    accountCode: pendingJournalEntryArgs.accountCode,
                    gridActionComplete: pendingJournalEntryArgs.gridActionComplete,
                    gridRef: pendingJournalEntryArgs.gridRef,
                });
                handleDiscardConfirmation();
            } else {
                closeEnterAmountsDialog(false, props.changeTrackingData);
            }
        } else {
            // Filter reset
            handleDiscardConfirmation();
        }
    };

    const renderedComponent: JSX.Element = (
        <>
            <Dialog open={true} onOpenChange={onDialogOpenChange}>
                <DialogSurface
                    id='enterAmountsDialog'
                    className={modalStyles}
                    aria-modal='false'
                    onKeyDown={syncfusionFocusHack}
                    onClick={onDialogSurfaceClick}
                    aria-labelledby='enterAmountsDialogTitle'
                >
                    <DialogBody className={dialogBodyStyles}>
                        <DialogTitle
                            action={
                                <DialogTrigger action='close'>
                                    <Button ref={cancelButtonRef} appearance='subtle' aria-label={closeLabel} icon={<Dismiss24Regular />} />
                                </DialogTrigger>
                            }
                            as='div'
                        >
                            {!journalEntryGridProps && (
                                <>
                                    <h2 className={headingStyles} id='enterAmountsDialogTitle'>
                                        {enterAmountsHeaderText}
                                    </h2>
                                    <div className={subHeadingStyles}>{props.rowDescription}</div>
                                </>
                            )}
                            {journalEntryGridProps && (
                                <>
                                    <h2 className={headingStyles} id='enterAmountsDialogTitle'>
                                        {journalEntryHeaderText}
                                    </h2>
                                    <div className={subHeadingStyles}>
                                        {`${t('entity')}: ${datasetEntity?.code} ${t('case')}: ${datasetCase?.code} ${t('jurisdiction')}: ${
                                            datasetJurisdiction?.code
                                        } ${t('year')}: ${datasetYear?.periodYear}`}
                                    </div>
                                </>
                            )}
                        </DialogTitle>
                        <DialogContent className={dialogContentStyles}>
                            {!isLoading && !journalEntryGridProps && (
                                <div id={enterAmountsModalId} className={enterAmountsDialogRootStyles}>
                                    <div className={filterColumnStyles}>
                                        <div className={filterWrapper}>
                                            <CalendarPeriodCombobox
                                                options={years}
                                                selectedOption={currentlyDisplayedYear}
                                                dispatch={dispatchFiltersUIState}
                                                key={currentlyDisplayedYear?.periodYear?.toString()}
                                            />
                                            <CaseCombobox
                                                options={cases}
                                                selectedOption={currentlyDisplayedCase}
                                                dispatch={dispatchFiltersUIState}
                                                key={currentlyDisplayedCase?.code}
                                            />
                                            <EntityCombobox
                                                options={entities}
                                                selectedOption={currentlyDisplayedEntity}
                                                dispatch={dispatchFiltersUIState}
                                                key={currentlyDisplayedEntity?.code}
                                            />
                                            <JurisdictionCombobox
                                                options={jurisdictions}
                                                selectedOption={currentlyDisplayedJurisdiction}
                                                dispatch={dispatchFiltersUIState}
                                                key={currentlyDisplayedJurisdiction?.code}
                                            />
                                            <AdjustmentCombobox
                                                options={adjustments}
                                                selectedOption={currentlyDisplayedAdjustment}
                                                dispatch={dispatchFiltersUIState}
                                                clearable={props.adjustmentCode ? false : true}
                                                key={currentlyDisplayedAdjustment?.adjustmentCode}
                                            />
                                            <div className={checkboxGroupStyles}>
                                                <Checkbox
                                                    label={t('amountsOnly').toString()}
                                                    checked={amountsOnly}
                                                    onChange={onAmountsOnlyChange}
                                                    key={`amountsOnly-${amountsOnly}`}
                                                />
                                                <Checkbox
                                                    label={t('combineDebitCreditColumns').toString()}
                                                    checked={combineDebitCreditColumns}
                                                    onChange={onCombineDebitCreditColumnsChange}
                                                    key={`combineDebitCreditColumns-${combineDebitCreditColumns}`}
                                                />
                                                {isFilteredEnterAmountsEnabled &&
                                                    (!props.isInputRow ? (
                                                        <Tooltip
                                                            relationship='description'
                                                            content={t('showAllAccountsTooltip').toString()}
                                                            appearance='inverted'
                                                            withArrow
                                                        >
                                                            <div style={{ display: 'inline-block', cursor: 'not-allowed' }}>
                                                                <Checkbox
                                                                    label={t('showAllAccounts').toString()}
                                                                    checked={showAllAccounts}
                                                                    disabled={true}
                                                                    key={`showAllAccounts-${showAllAccounts}`}
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                    ) : (
                                                        <Checkbox
                                                            label={t('showAllAccounts').toString()}
                                                            checked={showAllAccounts}
                                                            onChange={onShowAllAccountsChange}
                                                            disabled={false}
                                                            key={`showAllAccounts-${showAllAccounts}`}
                                                        />
                                                    ))}
                                            </div>
                                        </div>
                                        <div className={filterButtonWrapper}>
                                            <Button
                                                appearance='primary'
                                                size='small'
                                                disabled={applyButtonIsDisabled}
                                                onClick={onFilterApply}
                                                aria-label={t('apply').toString()}
                                                data-analytics-button-name='enter_amounts_dialog_apply_filter'
                                            >
                                                {t('apply')}
                                            </Button>
                                            <Button
                                                appearance='secondary'
                                                size='small'
                                                onClick={handleFilterReset}
                                                aria-label={t('reset').toString()}
                                                data-analytics-button-name='enter_amounts_dialog_reset_filter'
                                            >
                                                {t('reset')}
                                            </Button>
                                        </div>
                                    </div>

                                    <div>
                                        <ColumnDividerLine />
                                    </div>
                                    <div className={gridWrapper}>
                                        <GridStateContextProvider grid={gridRef}>
                                            <GridComponent
                                                className={gridComponentCSSClass}
                                                dataSource={accountAmountRows}
                                                editSettings={editSettings}
                                                cellEdit={onCellEdit}
                                                ref={gridRef}
                                                cellSave={onCellSave}
                                                rowDataBound={onRowDataBound}
                                                created={onGridCreated}
                                                keyPressed={onKeyPressed}
                                                rowHeight={28}
                                                dataBound={onGridDataBound}
                                                allowPaging={true}
                                                pageSettings={{ pageSize: 1000 }}
                                                filterSettings={filterOptions}
                                                allowSorting={true}
                                                allowFiltering={true}
                                                actionComplete={syncfusionGridFilterDismissHack.gridActionComplete}
                                                height={'100%'}
                                                contextMenuItems={contextMenuItems}
                                                contextMenuClick={handleContextMenuItemClick}
                                                contextMenuOpen={handleContextMenuOpen}
                                            >
                                                <ColumnsDirective>
                                                    <ColumnDirective
                                                        field={accountAmountRowPropertyNames.accountCode}
                                                        headerText={t('account').toString()}
                                                        textAlign='Left'
                                                        type='string'
                                                        width='114'
                                                        allowEditing={false}
                                                        // if one of the columns is not designated as the primary key, aggregation will not work.
                                                        // without this, the sum will be calculated as the amount in the first cell multiplied
                                                        // by the number of rows in the table
                                                        isPrimaryKey={true}
                                                        filter={stringColumnFilterParams}
                                                        customAttributes={{ class: accountAmountRowPropertyNames.accountCode }}
                                                    />
                                                    <ColumnDirective
                                                        field={accountAmountRowPropertyNames.accountDescription}
                                                        headerText={t('description').toString()}
                                                        textAlign='Left'
                                                        type='string'
                                                        width='206'
                                                        allowEditing={false}
                                                        filter={stringColumnFilterParams}
                                                        customAttributes={{ class: accountAmountRowPropertyNames.accountDescription }}
                                                    />
                                                    <ColumnDirective
                                                        field={accountAmountRowPropertyNames.accountBalanceType}
                                                        headerText={t('type').toString()}
                                                        textAlign='Left'
                                                        type='string'
                                                        width='96'
                                                        allowEditing={false}
                                                        filter={stringColumnFilterParams}
                                                        customAttributes={{ class: accountAmountRowPropertyNames.accountBalanceType }}
                                                    />
                                                    <ColumnDirective
                                                        field={accountAmountRowPropertyNames.debitTotal}
                                                        headerText={t('debit').toString()}
                                                        textAlign='Right'
                                                        type='number'
                                                        width='140'
                                                        format={numberFormatOptions}
                                                        edit={numericEditParams}
                                                        editType='numericedit'
                                                        allowFiltering={false}
                                                        allowEditing={functionalAccess?.amountDataEntryModifyEnabled}
                                                        allowSorting={false}
                                                        customAttributes={{ class: accountAmountRowPropertyNames.debitTotal }}
                                                    />
                                                    <ColumnDirective
                                                        field={accountAmountRowPropertyNames.creditTotal}
                                                        headerText={t('credit').toString()}
                                                        textAlign='Right'
                                                        type='number'
                                                        width='140'
                                                        format={numberFormatOptions}
                                                        edit={numericEditParams}
                                                        editType='numericedit'
                                                        allowFiltering={false}
                                                        allowEditing={functionalAccess?.amountDataEntryModifyEnabled}
                                                        allowSorting={false}
                                                        customAttributes={{ class: accountAmountRowPropertyNames.creditTotal }}
                                                    />
                                                    <ColumnDirective
                                                        field={accountAmountRowPropertyNames.combinedTotal}
                                                        headerText={t('combinedDebitCreditHeader').toString()}
                                                        textAlign='Right'
                                                        type='number'
                                                        width='280'
                                                        format={adjustedAmountFormat}
                                                        edit={numericEditParams}
                                                        editType='numericedit'
                                                        allowFiltering={false}
                                                        allowEditing={functionalAccess?.amountDataEntryModifyEnabled}
                                                        allowSorting={false}
                                                        visible={false}
                                                        customAttributes={{ class: accountAmountRowPropertyNames.combinedTotal }}
                                                    />
                                                    <ColumnDirective
                                                        field={accountAmountRowPropertyNames.adjustedTotal}
                                                        headerText={t('adjustedTotal').toString()}
                                                        textAlign='Right'
                                                        type='number'
                                                        width='150'
                                                        format={adjustedAmountFormat}
                                                        edit={numericEditParams}
                                                        editType='numericedit'
                                                        allowFiltering={true}
                                                        allowSorting={false}
                                                        customAttributes={{ class: accountAmountRowPropertyNames.adjustedTotal }}
                                                    />
                                                </ColumnsDirective>
                                                <Inject services={[Edit, Aggregate, Page, Filter, Sort, ContextMenu]} />
                                                <AggregatesDirective>
                                                    <AggregateDirective>
                                                        <AggregateColumnsDirective>
                                                            <AggregateColumnDirective
                                                                footerTemplate={totalRowTemplate}
                                                                // the aggregation type doesn't matter. you just have to supply something so the row will render
                                                                type='Min'
                                                                // by picking the accountCode field, we make sure the template will render in the correct position within the grid
                                                                field={accountAmountRowPropertyNames.accountCode}
                                                            />
                                                            <AggregateColumnDirective
                                                                type='Sum'
                                                                field={accountAmountRowPropertyNames.debitTotal}
                                                                format={numberFormatOptions}
                                                            />
                                                            <AggregateColumnDirective
                                                                type='Sum'
                                                                field={accountAmountRowPropertyNames.creditTotal}
                                                                format={numberFormatOptions}
                                                            />
                                                            <AggregateColumnDirective
                                                                type='Sum'
                                                                field={accountAmountRowPropertyNames.combinedTotal}
                                                                format={adjustedAmountFormat}
                                                            />
                                                            <AggregateColumnDirective
                                                                type='Sum'
                                                                field={accountAmountRowPropertyNames.adjustedTotal}
                                                                format={adjustedAmountFormat}
                                                            />
                                                        </AggregateColumnsDirective>
                                                    </AggregateDirective>
                                                </AggregatesDirective>
                                            </GridComponent>
                                        </GridStateContextProvider>
                                    </div>
                                </div>
                            )}
                            {!isLoading && journalEntryGridProps && <JournalEntryGrid {...journalEntryGridProps} />}

                            {isLoading && (
                                <div className={spinnerContainerClassName}>
                                    <LoadingSpinner id='loadingSpinnerEnterAmountsGrid' label={t('loadingPleaseWait').toString()} />
                                </div>
                            )}
                        </DialogContent>
                        <DialogActions>
                            {!journalEntryGridProps && (
                                <>
                                    <Button
                                        appearance='primary'
                                        onClick={primaryHandler}
                                        disabled={saveButtonIsDisabled}
                                        aria-label={confirmLabel}
                                        data-analytics-button-name='enter_amounts_dialog_save_amounts'
                                    >
                                        {confirmLabel}
                                    </Button>
                                    <DialogTrigger>
                                        <Button aria-label={cancelLabel} data-analytics-button-name='enter_amounts_dialog_cancel'>
                                            {cancelLabel}
                                        </Button>
                                    </DialogTrigger>
                                </>
                            )}
                            {journalEntryGridProps && (
                                <Button
                                    onClick={() => updateDialogState({ type: EnterAmountsModalActionActionType.JournalEntryClosed })}
                                    aria-label={backLabel}
                                >
                                    {backLabel}
                                </Button>
                            )}
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
            {showConfirmationDialog && (
                <ConfirmationDialogV9
                    title={confirmationDialogType === ConfirmationDialogType.UnsavedChanges ? t('confirm') : t('saveChanges')}
                    content={t('unsavedAmountsConfirmationMessage')}
                    buttons={
                        <>
                            <DialogTrigger>
                                <Button
                                    onClick={onConfirmationCancel}
                                    data-analytics-button-name='confirmation_dialog_cancel'
                                    aria-label={t('cancel').toString()}
                                >
                                    {t('cancel')}
                                </Button>
                            </DialogTrigger>
                            <Button
                                appearance='primary'
                                onClick={onConfirmationSave}
                                data-analytics-button-name='confirmation_dialog_save'
                                aria-label={t('save').toString()}
                            >
                                {t('save')}
                            </Button>
                            <Button
                                onClick={onConfirmationDismiss}
                                data-analytics-button-name='confirmation_dialog_discard'
                                aria-label={(confirmationDialogType === ConfirmationDialogType.UnsavedChanges
                                    ? t('leave')
                                    : t('discard')
                                ).toString()}
                            >
                                {confirmationDialogType === ConfirmationDialogType.UnsavedChanges ? t('leave') : t('discard')}
                            </Button>
                        </>
                    }
                />
            )}
        </>
    );
    if (!isLoading && loadStateChangeEvent) {
        if (loadStateChangeEvent === 'Initialize') {
            const trackingInformation: IEnterAmountsDialogInitialLoadTrackingInformation =
                getEnterAmountsDialogInitialLoadTrackingInformation()!;
            trackingInformation.numberOfAccounts = accountAmountRows?.length ?? 0;
            trackingInformation.success = true;
            trackEnterAmountsDialogInitialLoadEnd();
        } else if (loadStateChangeEvent === 'FilterChange') {
            const trackingInformation: IEnterAmountsDialogFilterChangeTrackingInformation =
                getEnterAmountsDialogFilterChangeTrackingInformation()!;
            trackingInformation.numberOfAccounts = accountAmountRows?.length ?? 0;
            trackingInformation.success = true;
            trackEnterAmountsDialogFilterChangeEnd();
        } else if (loadStateChangeEvent === 'SaveAmounts') {
            trackEnterAmountsDialogSaveAmountsEnd();
        }
    }
    return renderedComponent;
};

export default EnterAmountsDialog;
